/**
 * DXF reader (ASCII DXF, AutoCAD R12 to 2018+) for architects' floor plans: model-space lines,
 * polylines (with arcs), circles, arcs, ellipses, splines, solids, leaders, texts and block
 * references (nested, scaled, rotated, arrayed), dimensions (their blocks). Everything becomes
 * polylines and texts in drawing units, by layer. Hatches, 3D entities, images and paper space
 * are left out. DWG is a closed binary format: it is recognised, not read.
 */
import type { PlanLayer, PlanText, VectorPlan } from './vector.ts';

export class CadFormatError extends Error {
  readonly kind: 'dwg' | 'dxf-binary' | 'not-dxf';
  readonly detail: string;
  constructor(kind: 'dwg' | 'dxf-binary' | 'not-dxf', detail = '') {
    super(kind === 'dwg' ? `DWG ${detail}` : kind);
    this.kind = kind;
    this.detail = detail;
  }
}

/** DWG release names by file signature. */
const DWG_VERSIONS: Record<string, string> = {
  AC1012: 'R13',
  AC1014: 'R14',
  AC1015: '2000',
  AC1018: '2004',
  AC1021: '2007',
  AC1024: '2010',
  AC1027: '2013',
  AC1032: '2018',
};

export function detectCad(bytes: Uint8Array): 'dxf' | 'dxf-binary' | 'dwg' | 'unknown' {
  const head = new TextDecoder('latin1').decode(bytes.subarray(0, 32));
  if (head.startsWith('AutoCAD Binary DXF')) return 'dxf-binary';
  if (/^AC10\d\d/.test(head)) return 'dwg';
  if (/^\s*0\s*\r?\n\s*SECTION/.test(head) || /^\s*999\s*\r?\n/.test(head)) return 'dxf';
  return 'unknown';
}

/** Millimetres per drawing unit for $INSUNITS values. */
export const INSUNITS_MM: Record<number, number> = {
  1: 25.4,
  2: 304.8,
  3: 1_609_344,
  4: 1,
  5: 10,
  6: 1000,
  7: 1_000_000,
  8: 0.0000254,
  9: 0.0254,
  10: 914.4,
  14: 100,
};

export interface DxfLayerInfo {
  name: string;
  /** Polylines and texts found on it (model space, after expanding blocks). */
  lines: number;
  texts: number;
  /** Off or frozen in the file. */
  hidden: boolean;
}

export interface DxfDrawing {
  /** $ACADVER, e.g. AC1027. */
  version: string;
  /** $INSUNITS (0 = not stated). */
  insunits: number;
  layers: DxfLayerInfo[];
  /** Geometry in drawing units, every layer (hidden ones included). */
  plan: VectorPlan;
  /** Entity types skipped, with counts. */
  skipped: Record<string, number>;
  /** Elements far away from the drawing (stray CAD objects), left out. */
  strays: number;
}

// ---------- low level ----------

type Pair = [number, string];

function decode(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes); // DXF before 2007: ANSI code page
  }
}

function pairs(text: string): Pair[] {
  const lines = text.split(/\r\n|\r|\n/);
  const out: Pair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = Number(lines[i]!.trim());
    if (!Number.isFinite(code)) throw new CadFormatError('not-dxf');
    out.push([code, lines[i + 1]!.replace(/^\s+|\s+$/g, '')]);
  }
  return out;
}

interface Entity {
  type: string;
  codes: Pair[];
  /** POLYLINE vertices / INSERT attributes. */
  children: Entity[];
}

const num = (e: Entity, code: number, d = 0) => {
  const p = e.codes.find((c) => c[0] === code);
  return p ? Number(p[1]) || 0 : d;
};
const str = (e: Entity, code: number, d = '') => e.codes.find((c) => c[0] === code)?.[1] ?? d;
const all = (e: Entity, code: number) => e.codes.filter((c) => c[0] === code).map((c) => Number(c[1]) || 0);

/** Group pairs (from a 0 code to the next) into entities; POLYLINE/INSERT own what follows until SEQEND. */
function entities(ps: Pair[], from: number, to: number): Entity[] {
  const out: Entity[] = [];
  let cur: Entity | null = null;
  let owner: Entity | null = null;
  for (let i = from; i < to; i++) {
    const [code, value] = ps[i]!;
    if (code === 0) {
      if (value === 'SEQEND') {
        owner = null;
        cur = null;
        continue;
      }
      const e: Entity = { type: value, codes: [], children: [] };
      if (owner && (value === 'VERTEX' || value === 'ATTRIB')) owner.children.push(e);
      else {
        owner = null;
        out.push(e);
        if (value === 'POLYLINE') owner = e;
      }
      cur = e;
      continue;
    }
    if (!cur) continue;
    cur.codes.push([code, value]);
    if (cur.type === 'INSERT' && code === 66 && value === '1') owner = cur;
  }
  return out;
}

// ---------- geometry ----------

/** 2D affine transform [a, b, c, d, e, f]: x' = a x + c y + e, y' = b x + d y + f. */
type M = [number, number, number, number, number, number];
const I: M = [1, 0, 0, 1, 0, 0];
const mul = (m: M, n: M): M => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];
const apply = (m: M, x: number, y: number): [number, number] => [
  m[0] * x + m[2] * y + m[4],
  m[1] * x + m[3] * y + m[5],
];
const scaleOf = (m: M) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
const angleOf = (m: M) => (Math.atan2(m[1], m[0]) * 180) / Math.PI;

const STEP = Math.PI / 36; // 5°

function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number): number[] {
  let sweep = a1 - a0;
  while (sweep <= 0) sweep += Math.PI * 2;
  const n = Math.max(2, Math.ceil(sweep / STEP));
  const pts: number[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + (sweep * i) / n;
    pts.push(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  return pts;
}

/** Points of a bulged segment p → q (bulge = tan(θ/4)), excluding p. */
function bulgePoints(px: number, py: number, qx: number, qy: number, b: number): number[] {
  if (Math.abs(b) < 1e-9) return [qx, qy];
  const theta = 4 * Math.atan(b);
  const dx = qx - px;
  const dy = qy - py;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-12) return [qx, qy];
  const r = chord / (2 * Math.sin(Math.abs(theta) / 2));
  // Centre: on the perpendicular bisector, side given by the bulge sign.
  const mx = (px + qx) / 2;
  const my = (py + qy) / 2;
  const h = Math.sqrt(Math.max(0, r * r - (chord / 2) ** 2));
  const s = (b > 0 ? 1 : -1) * (Math.abs(theta) > Math.PI ? -1 : 1);
  const cx = mx - (s * h * dy) / chord;
  const cy = my + (s * h * dx) / chord;
  const a0 = Math.atan2(py - cy, px - cx);
  const n = Math.max(2, Math.ceil(Math.abs(theta) / STEP));
  const out: number[] = [];
  for (let i = 1; i <= n; i++) {
    const a = a0 + (theta * i) / n;
    out.push(cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  out[out.length - 2] = qx;
  out[out.length - 1] = qy;
  return out;
}

function polyFromVertices(vs: Array<{ x: number; y: number; b: number }>, closed: boolean): number[] {
  if (!vs.length) return [];
  const pts = [vs[0]!.x, vs[0]!.y];
  const n = closed ? vs.length : vs.length - 1;
  for (let i = 0; i < n; i++) {
    const p = vs[i]!;
    const q = vs[(i + 1) % vs.length]!;
    pts.push(...bulgePoints(p.x, p.y, q.x, q.y, p.b));
  }
  return pts;
}

/** NURBS curve by de Boor, sampled. */
function splinePoints(
  degree: number,
  knots: number[],
  cps: Array<[number, number]>,
  weights: number[],
): number[] {
  const n = cps.length;
  const p = Math.max(1, Math.min(degree, n - 1));
  if (n < 2) return [];
  let U = knots;
  if (U.length !== n + p + 1) {
    // Missing or odd knots: clamped uniform.
    U = [];
    for (let i = 0; i <= n + p; i++) U.push(i <= p ? 0 : i >= n ? n - p : i - p);
  }
  const w = weights.length === n ? weights : cps.map(() => 1);
  const t0 = U[p]!;
  const t1 = U[n]!;
  const samples = Math.min(400, Math.max(16, n * 8));
  const out: number[] = [];
  for (let s = 0; s <= samples; s++) {
    const t = s === samples ? t1 - 1e-9 * (t1 - t0) : t0 + ((t1 - t0) * s) / samples;
    let k = p;
    while (k < n - 1 && t >= U[k + 1]!) k++;
    const d = Array.from({ length: p + 1 }, (_, j) => {
      const i = k - p + j;
      const [x, y] = cps[i]!;
      const wi = w[i]!;
      return [x * wi, y * wi, wi] as [number, number, number];
    });
    for (let r = 1; r <= p; r++)
      for (let j = p; j >= r; j--) {
        const i = k - p + j;
        const den = U[i + p - r + 1]! - U[i]!;
        const a = den === 0 ? 0 : (t - U[i]!) / den;
        const prev = d[j - 1]!;
        const cur = d[j]!;
        d[j] = [
          (1 - a) * prev[0] + a * cur[0],
          (1 - a) * prev[1] + a * cur[1],
          (1 - a) * prev[2] + a * cur[2],
        ];
      }
    const [x, y, ww] = d[p]!;
    out.push(x / (ww || 1), y / (ww || 1));
  }
  return out;
}

/** MTEXT/TEXT content to plain lines. */
export function cleanText(s: string, mtext: boolean): string[] {
  let t = s
    .replace(/\\U\+([0-9A-Fa-f]{4})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)))
    .replace(/%%[cC]/g, 'Ø')
    .replace(/%%[dD]/g, '°')
    .replace(/%%[pP]/g, '±')
    .replace(/%%[uUoOkK]/g, '')
    .replace(/%%(\d{3})/g, (_, d: string) => String.fromCharCode(Number(d)));
  if (!mtext) return [t];
  t = t
    .replace(/\\[Pp]/g, '\n')
    .replace(/\\~/g, ' ')
    .replace(/\\[ACcFfHhQqTtWw][^;]*;/g, '')
    .replace(/\\S([^;]*?)[\^#/]([^;]*);/g, '$1/$2')
    .replace(/\\[LlOoKkNX]/g, '')
    .replace(/\\\\/g, '\\')
    .replace(/[{}]/g, '');
  return t.split('\n').map((l) => l.trim());
}

// ---------- reader ----------

interface Block {
  base: [number, number];
  entities: Entity[];
}

export function parseDxf(bytes: Uint8Array): DxfDrawing {
  const kind = detectCad(bytes);
  if (kind === 'dwg') {
    const sig = new TextDecoder('latin1').decode(bytes.subarray(0, 6));
    throw new CadFormatError('dwg', DWG_VERSIONS[sig] ?? sig);
  }
  if (kind === 'dxf-binary') throw new CadFormatError('dxf-binary');
  const ps = pairs(decode(bytes));
  const header: Record<string, Pair[]> = {};
  const layerState = new Map<string, boolean>(); // name → hidden
  const blocks = new Map<string, Block>();
  let modelEntities: Entity[] = [];

  for (let i = 0; i < ps.length; i++) {
    if (ps[i]![0] !== 0 || ps[i]![1] !== 'SECTION') continue;
    const name = ps[i + 1]?.[1];
    let end = i + 2;
    while (end < ps.length && !(ps[end]![0] === 0 && ps[end]![1] === 'ENDSEC')) end++;
    if (name === 'HEADER') {
      let v = '';
      for (let j = i + 2; j < end; j++) {
        const [c, val] = ps[j]!;
        if (c === 9) {
          v = val;
          header[v] = [];
        } else if (v) header[v]!.push([c, val]);
      }
    } else if (name === 'TABLES') {
      for (const e of entities(ps, i + 2, end))
        if (e.type === 'LAYER') {
          const flags = num(e, 70);
          layerState.set(str(e, 2), num(e, 62, 7) < 0 || (flags & 1) === 1);
        }
    } else if (name === 'BLOCKS') {
      let cur: { name: string; block: Block } | null = null;
      for (const e of entities(ps, i + 2, end)) {
        if (e.type === 'BLOCK') {
          cur = { name: str(e, 2), block: { base: [num(e, 10), num(e, 20)], entities: [] } };
          blocks.set(cur.name, cur.block);
        } else if (e.type === 'ENDBLK') cur = null;
        else cur?.block.entities.push(e);
      }
    } else if (name === 'ENTITIES') modelEntities = entities(ps, i + 2, end);
    i = end;
  }

  const layers = new Map<string, PlanLayer>();
  const layerOf = (name: string) => {
    let l = layers.get(name);
    if (!l) layers.set(name, (l = { name, lines: [], texts: [] }));
    return l;
  };
  const skipped: Record<string, number> = {};
  const skip = (t: string) => (skipped[t] = (skipped[t] ?? 0) + 1);
  let budget = 3_000_000; // points

  const emit = (layer: string, m: M, ocsFlip: boolean, pts: number[], closed = false) => {
    if (pts.length < 4 || budget <= 0) return;
    const out: number[] = new Array(pts.length + (closed ? 2 : 0));
    for (let i = 0; i < pts.length; i += 2) {
      const [x, y] = apply(m, ocsFlip ? -pts[i]! : pts[i]!, pts[i + 1]!);
      out[i] = x;
      out[i + 1] = y;
    }
    if (closed) {
      out[pts.length] = out[0]!;
      out[pts.length + 1] = out[1]!;
    }
    budget -= out.length / 2;
    layerOf(layer).lines.push(out);
  };

  const text = (layer: string, m: M, ocsFlip: boolean, t: PlanText) => {
    if (!t.text.trim()) return;
    const [x, y] = apply(m, ocsFlip ? -t.x : t.x, t.y);
    layerOf(layer).texts.push({ ...t, x, y, h: t.h * scaleOf(m), rot: t.rot + angleOf(m) });
  };

  const walk = (list: Entity[], m: M, parentLayer: string, depth: number) => {
    for (const e of list) {
      if (num(e, 67) === 1) continue; // paper space
      const own = str(e, 8, '0');
      const layer = own === '0' && parentLayer ? parentLayer : own;
      const flip = num(e, 230, 1) < 0;
      switch (e.type) {
        case 'LINE':
          emit(layer, m, flip, [num(e, 10), num(e, 20), num(e, 11), num(e, 21)]);
          break;
        case 'LWPOLYLINE': {
          const xs = all(e, 10);
          const ys = all(e, 20);
          // Bulges belong to the vertex they follow; walk codes in order.
          const vs: Array<{ x: number; y: number; b: number }> = [];
          for (const [c, v] of e.codes) {
            if (c === 10) vs.push({ x: Number(v) || 0, y: 0, b: 0 });
            else if (c === 20 && vs.length) vs[vs.length - 1]!.y = Number(v) || 0;
            else if (c === 42 && vs.length) vs[vs.length - 1]!.b = Number(v) || 0;
          }
          if (vs.length !== xs.length || xs.length !== ys.length) break;
          emit(layer, m, flip, polyFromVertices(vs, (num(e, 70) & 1) === 1));
          break;
        }
        case 'POLYLINE': {
          const flags = num(e, 70);
          if (flags & (16 | 64)) {
            skip('POLYLINE-mesh');
            break;
          }
          const vs = e.children
            .filter((v) => (num(v, 70) & 16) === 0) // not spline frame control points
            .map((v) => ({ x: num(v, 10), y: num(v, 20), b: num(v, 42) }));
          emit(layer, m, flip, polyFromVertices(vs, (flags & 1) === 1));
          break;
        }
        case 'CIRCLE': {
          const r = num(e, 40);
          if (r > 0) emit(layer, m, flip, arcPoints(num(e, 10), num(e, 20), r, 0, Math.PI * 2));
          break;
        }
        case 'ARC': {
          const r = num(e, 40);
          const a0 = (num(e, 50) * Math.PI) / 180;
          const a1 = (num(e, 51) * Math.PI) / 180;
          if (r > 0) emit(layer, m, flip, arcPoints(num(e, 10), num(e, 20), r, a0, a1));
          break;
        }
        case 'ELLIPSE': {
          const cx = num(e, 10);
          const cy = num(e, 20);
          const mx = num(e, 11);
          const my = num(e, 21);
          const ratio = num(e, 40, 1);
          const t0 = num(e, 41, 0);
          let t1 = num(e, 42, Math.PI * 2);
          if (t1 <= t0) t1 += Math.PI * 2;
          const n = Math.max(8, Math.ceil((t1 - t0) / STEP));
          const pts: number[] = [];
          for (let i = 0; i <= n; i++) {
            const t = t0 + ((t1 - t0) * i) / n;
            pts.push(
              cx + Math.cos(t) * mx - Math.sin(t) * ratio * my,
              cy + Math.cos(t) * my + Math.sin(t) * ratio * mx,
            );
          }
          emit(layer, m, flip, pts);
          break;
        }
        case 'SPLINE': {
          const xs = all(e, 10);
          const ys = all(e, 20);
          const cps = xs.map((x, i) => [x, ys[i] ?? 0] as [number, number]);
          if (cps.length >= 2) emit(layer, m, flip, splinePoints(num(e, 71, 3), all(e, 40), cps, all(e, 41)));
          else {
            const fx = all(e, 11);
            const fy = all(e, 21);
            emit(
              layer,
              m,
              flip,
              fx.flatMap((x, i) => [x, fy[i] ?? 0]),
            );
          }
          break;
        }
        case 'SOLID':
        case 'TRACE': {
          const p = [
            num(e, 10),
            num(e, 20),
            num(e, 11),
            num(e, 21),
            num(e, 13),
            num(e, 23),
            num(e, 12),
            num(e, 22),
          ];
          emit(layer, m, flip, p, true);
          break;
        }
        case 'LEADER': {
          const xs = all(e, 10);
          const ys = all(e, 20);
          emit(
            layer,
            m,
            flip,
            xs.flatMap((x, i) => [x, ys[i] ?? 0]),
          );
          break;
        }
        case 'TEXT':
        case 'ATTRIB':
        case 'ATTDEF': {
          if (e.type === 'ATTDEF') break; // definitions: shown through ATTRIBs
          if (e.type === 'ATTRIB' && num(e, 70) & 1) break; // invisible attribute
          const h = num(e, 40, 2.5);
          const hj = num(e, 72);
          const vj = num(e, 73, e.type === 'ATTRIB' ? num(e, 74) : 0);
          const useSecond = hj !== 0 || vj !== 0;
          const x = useSecond ? num(e, 11, num(e, 10)) : num(e, 10);
          const y = useSecond ? num(e, 21, num(e, 20)) : num(e, 20);
          const [line = ''] = cleanText(str(e, 1), false);
          const align = hj === 1 || hj === 4 ? 'middle' : hj === 2 ? 'end' : 'start';
          text(layer, m, flip, { x, y, h, rot: num(e, 50), text: line, align });
          break;
        }
        case 'MTEXT': {
          const h = num(e, 40, 2.5);
          const att = num(e, 71, 1);
          const raw = e.codes
            .filter((c) => c[0] === 3)
            .map((c) => c[1])
            .join('');
          const lines = cleanText(raw + str(e, 1), true);
          const dirX = num(e, 11, 1);
          const dirY = num(e, 21, 0);
          const rot = e.codes.some((c) => c[0] === 50)
            ? num(e, 50)
            : (Math.atan2(dirY, dirX) * 180) / Math.PI;
          const col = (att - 1) % 3;
          const rowAt = Math.floor((att - 1) / 3); // 0 top, 1 middle, 2 bottom
          const gap = h * 1.5;
          const total = (lines.length - 1) * gap;
          const firstBase = rowAt === 0 ? -h : rowAt === 1 ? total / 2 - h / 2 : total;
          const a = (rot * Math.PI) / 180;
          lines.forEach((l, i) => {
            const dy = firstBase - i * gap;
            text(layer, m, flip, {
              x: num(e, 10) - Math.sin(a) * dy,
              y: num(e, 20) + Math.cos(a) * dy,
              h,
              rot,
              text: l,
              align: col === 0 ? 'start' : col === 1 ? 'middle' : 'end',
            });
          });
          break;
        }
        case 'INSERT':
        case 'DIMENSION': {
          const name = str(e, 2);
          const block = blocks.get(name);
          if (!block || depth > 8 || /^\*paper_space/i.test(name)) break;
          if (e.type === 'DIMENSION') {
            // Dimension blocks are drawn in world coordinates already.
            walk(block.entities, m, layer, depth + 1);
            break;
          }
          const sx = num(e, 41, 1);
          const sy = num(e, 42, 1);
          const rot = (num(e, 50) * Math.PI) / 180;
          const cols = Math.max(1, num(e, 70, 1));
          const rows = Math.max(1, num(e, 71, 1));
          const dc = num(e, 44);
          const dr = num(e, 45);
          const ix = num(e, 10);
          const iy = num(e, 20);
          const cos = Math.cos(rot);
          const sin = Math.sin(rot);
          for (let r = 0; r < Math.min(rows, 100); r++)
            for (let c = 0; c < Math.min(cols, 100); c++) {
              const ox = ix + cos * c * dc - sin * r * dr;
              const oy = iy + sin * c * dc + cos * r * dr;
              let local: M = [cos * sx, sin * sx, -sin * sy, cos * sy, ox, oy];
              local = mul(local, [1, 0, 0, 1, -block.base[0], -block.base[1]]);
              // Inserted in a mirrored coordinate system (extrusion 0,0,-1): mirror the whole block.
              if (flip) local = mul([-1, 0, 0, 1, 0, 0], local);
              walk(block.entities, mul(m, local), layer, depth + 1);
            }
          // Attributes are positioned in world (OCS) coordinates.
          walk(e.children, m, layer, depth + 1);
          break;
        }
        case 'POINT':
        case 'VIEWPORT':
        case 'SEQEND':
          break;
        default:
          skip(e.type);
      }
    }
  };
  walk(modelEntities, I, '', 0);

  const plan: VectorPlan = { v: 1, bounds: [0, 0, 0, 0], layers: [...layers.values()] };
  const strays = trimStrays(plan);
  plan.bounds = boundsOf(plan);
  const h = (v: string) => header[v] ?? [];
  return {
    version: h('$ACADVER').find((c) => c[0] === 1)?.[1] ?? '',
    insunits: Number(h('$INSUNITS').find((c) => c[0] === 70)?.[1] ?? 0) || 0,
    layers: plan.layers
      .map((l) => ({
        name: l.name,
        lines: l.lines.length,
        texts: l.texts.length,
        hidden: !!layerState.get(l.name),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    plan,
    skipped,
    strays,
  };
}

export function boundsOf(p: VectorPlan): VectorPlan['bounds'] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const l of p.layers) {
    for (const pl of l.lines)
      for (let i = 0; i < pl.length; i += 2) {
        const x = pl[i]!;
        const y = pl[i + 1]!;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    for (const t of l.texts) {
      if (t.x < minX) minX = t.x;
      if (t.x > maxX) maxX = t.x;
      if (t.y < minY) minY = t.y;
      if (t.y > maxY) maxY = t.y;
    }
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : [0, 0, 1, 1];
}

/**
 * CAD files often carry a few objects far away from the drawing (a stray line at the origin,
 * an old block). They would shrink the plan to a dot: keep what lies around the bulk of the
 * points (1st–99th percentile box, widened by half its size). Returns how many were dropped.
 */
function trimStrays(p: VectorPlan): number {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const l of p.layers)
    for (const pl of l.lines)
      for (let i = 0; i < pl.length; i += 2) {
        xs.push(pl[i]!);
        ys.push(pl[i + 1]!);
      }
  if (xs.length < 50) return 0;
  const q = (a: number[], f: number) => a[Math.min(a.length - 1, Math.floor(a.length * f))]!;
  xs.sort((a, b) => a - b);
  ys.sort((a, b) => a - b);
  const [x0, x1, y0, y1] = [q(xs, 0.01), q(xs, 0.99), q(ys, 0.01), q(ys, 0.99)];
  const wx = Math.max(x1 - x0, 1) * 0.5;
  const wy = Math.max(y1 - y0, 1) * 0.5;
  const inside = (x: number, y: number) => x >= x0 - wx && x <= x1 + wx && y >= y0 - wy && y <= y1 + wy;
  let dropped = 0;
  for (const l of p.layers) {
    const keep = l.lines.filter((pl) => {
      for (let i = 0; i < pl.length; i += 2) if (inside(pl[i]!, pl[i + 1]!)) return true;
      return false;
    });
    dropped += l.lines.length - keep.length;
    l.lines = keep;
    const texts = l.texts.filter((t) => inside(t.x, t.y));
    dropped += l.texts.length - texts.length;
    l.texts = texts;
  }
  return dropped;
}

/** Millimetres per drawing unit: from $INSUNITS, else guessed from the size of a floor plan. */
export function unitMm(d: DxfDrawing): { mm: number; guessed: boolean } {
  const stated = INSUNITS_MM[d.insunits];
  if (stated) return { mm: stated, guessed: false };
  const [x0, y0, x1, y1] = d.plan.bounds;
  const size = Math.max(x1 - x0, y1 - y0);
  // A house or a floor is 5–100 m across.
  if (size <= 200) return { mm: 1000, guessed: true }; // metres
  if (size <= 20_000) return { mm: 10, guessed: true }; // centimetres
  return { mm: 1, guessed: true };
}

/** The plan to store: chosen layers only, in millimetres. */
export function dxfToPlan(
  d: DxfDrawing,
  opts: { layers: ReadonlySet<string>; mmPerUnit: number },
): VectorPlan {
  const k = opts.mmPerUnit;
  const layers = d.plan.layers
    .filter((l) => opts.layers.has(l.name))
    .map((l) => ({
      name: l.name,
      lines: l.lines.map((pl) => pl.map((v) => v * k)),
      texts: l.texts.map((t) => ({ ...t, x: t.x * k, y: t.y * k, h: t.h * k })),
    }));
  const plan: VectorPlan = { v: 1, bounds: [0, 0, 1, 1], layers };
  plan.bounds = boundsOf(plan);
  return plan;
}

/** Smallest usual drawing scale at which the plan fits in a sheet area (mm). */
export function fittingScale(plan: VectorPlan, area: { width: number; height: number }): number {
  const w = plan.bounds[2] - plan.bounds[0];
  const h = plan.bounds[3] - plan.bounds[1];
  for (const s of [20, 25, 50, 75, 100, 125, 200, 250, 500, 1000])
    if (w / s <= area.width && h / s <= area.height) return s;
  return 1000;
}
